import { Action, Dispatch } from 'redux';
import type { Range } from '@bygghemmait/components/form';
import { FacetFilter } from 'models/FacetFilter';
import { PageState } from 'models/PageState';
import { createHref, IHref } from 'components/Shared/server';
import { historyPushState } from 'store/history/actions';
import { reduxDispatchEvent } from 'components/Shared/communication';
import { identity, invokeIfExists } from 'utils';
import { ProductlistPageModel } from 'models/ProductlistPageModel';
import { FilterValue } from 'models/FilterValue';
import { isFilterPage, isValueFacetFilter } from 'components/Filter/selector';
import { FilterDisplayType } from 'models/FilterDisplayType';
import { FacetGroup } from 'models/FacetGroup';
import { FilterValueWithChildren } from 'models/FilterValueWithChildren';
import { GtmFilterChange } from 'store/googleTagManager/actions';
import { ChoiceFilter } from 'components/Filter/utils';

export const FILTER_UPDATE_PANELS = 'FILTER_UPDATE_PANELS';
export const FILTER_TOGGLE_RESET = 'FILTER_TOGGLE_RESET';
export const FILTER_TOGGLE = 'FILTER_TOGGLE';
export const FILTER_TOGGLE_CATEGORY = 'FILTER_TOGGLE_CATEGORY';
export const FILTER_RANGE = 'FILTER_RANGE';

export type FilterToggleCategoryParams = {
  facet: string;
  facets?: ChoiceFilter[];
};
export interface FilterToggleParams {
  facet: string;
  filter: FilterValue | FilterValueWithChildren;
  parent?: FilterValueWithChildren;
}

export interface FilterToggleWithChildrenParams {
  facet: string;
  filter: FilterValueWithChildren | FilterValue;
  parent?: FilterValueWithChildren;
}
export interface FilterRangeParams {
  facet: string;
  value: Range;
}
export type FilterTypeParams = FilterToggleCategoryParams | FilterToggleParams | FilterRangeParams;
export type FilterToggleAction = Action &
  (FilterToggleCategoryParams | FilterToggleParams | FilterRangeParams);

interface ShouldToggleParentArgs {
  filter: FilterValue;
  parent?: FilterValueWithChildren;
}
const shouldToggleParent = ({ filter, parent }: ShouldToggleParentArgs) => {
  if (!parent) {
    return false;
  }

  const shouldToggleOn = parent.subValues.every(f =>
    f.value === filter.value ? !f.selected : f.selected
  );

  const shouldToggleOff = parent.subValues.every(f => f.selected);

  return shouldToggleOff || shouldToggleOn;
};

const toValue = (filter: FilterValue) => filter.value;

const getSubValues = (filter: FilterValueWithChildren) =>
  filter.subValues
    .filter(f => !f.selected)
    .map(toValue)
    .join(TOGGLE_SEPARATOR);

const TOGGLE_SEPARATOR = '|';
export const formatToggleValue = (
  location: IHref,
  { facet, filter, parent }: FilterToggleParams,
  categoryFilterName?: string
) => {
  const toggleParent = shouldToggleParent({ filter, parent });

  const remove = () => {
    // Filter can contain a value such as '1|2|3', in that case we want to remove
    // it before we split the string

    if (!location.search[facet]) {
      return '';
    }

    let values = filter.value.includes(TOGGLE_SEPARATOR)
      ? location.search[facet].replace(filter.value, '').split(TOGGLE_SEPARATOR)
      : location.search[facet].split(TOGGLE_SEPARATOR);

    if (!hasChildren(filter)) {
      if (toggleParent && parent) {
        values = values.filter(value => value !== parent.value);
      }
      return values
        .filter(value => filter.value !== value && identity(value))
        .join(TOGGLE_SEPARATOR);
    }

    return values
      .filter(
        value =>
          value !== filter.value &&
          !filter.subValues.find(s => s.value === value) &&
          identity(value)
      )
      .join(TOGGLE_SEPARATOR);
  };
  const add = () => {
    const facetExists = !!location.search[facet];

    if (!facetExists && hasChildren(filter) && filter.subValues.length > 0) {
      return `${filter.value}${TOGGLE_SEPARATOR}${getSubValues(filter)}`;
    }
    if (!facetExists) {
      return categoryFilterName
        ? `${filter.value}${TOGGLE_SEPARATOR}${categoryFilterName}`
        : filter.value;
    }

    let filterString = location.search[facet] + `${TOGGLE_SEPARATOR}${filter.value}`;

    if (hasChildren(filter) && filter.subValues.length > 0) {
      filterString = `${filterString}${TOGGLE_SEPARATOR}${getSubValues(filter)}`;
    }

    if (toggleParent && parent) {
      filterString += `${TOGGLE_SEPARATOR}${parent.value}`;
    }

    return filterString;
  };
  return filter.selected ? remove() : add();
};

const hasChildren = (filter: FilterValue): filter is FilterValueWithChildren =>
  (filter as FilterValueWithChildren).subValues !== undefined;

const RANGE_SEPARATOR = ':';
const formatRangeValue = ({ value: { min, max } }: FilterRangeParams) =>
  `${min}${RANGE_SEPARATOR}${max}`;

export const filterToggle =
  (args: FilterToggleParams) =>
  (dispatch: Dispatch, getState: () => PageState): void => {
    let location = createHref(getState().location);
    const { categoryFilterFacet, categoryFilterName, isCategoryFilterPage } = getState()
      .currentPage as ProductlistPageModel;

    if (args.filter.url && isCategoryFilterPage && args.filter.displayName === categoryFilterName) {
      location = createHref({ ...getState().location, pathname: args.filter.url });
      if (location.search[args.facet]) {
        location.search[args.facet] = formatToggleValue(location, args);
      }
    } else {
      const shouldAppendCategoryFilter = isCategoryFilterPage && args.facet === categoryFilterFacet;
      const value = formatToggleValue(
        location,
        args,
        shouldAppendCategoryFilter ? categoryFilterName : ''
      );

      location.search[args.facet] = value;
    }

    if (!location.search[args.facet]) {
      delete location.search[args.facet];
    }

    void reduxDispatchEvent(historyPushState({ url: location }));
    dispatch(GtmFilterChange());

    dispatch({
      type: FILTER_TOGGLE,
      ...args
    });
  };

export const filterRange =
  (args: FilterRangeParams) => (dispatch: Dispatch, getState: () => PageState) => {
    const location = createHref(getState().location);
    location.search[args.facet] = formatRangeValue(args);

    void reduxDispatchEvent(historyPushState({ url: location }));
    dispatch(GtmFilterChange());

    dispatch({
      type: FILTER_RANGE,
      ...args
    });
  };

export const updateFilterPanels = (filterPanels: FacetFilter[]) => ({
  type: FILTER_UPDATE_PANELS,
  filterPanels
});

interface GetCategoryFilterPathnameArgs {
  filterPanels: FacetFilter[];
  categoryFilterFacet: string;
  categoryFilterName: string;
}
export const getCategoryFilterPathname = ({
  filterPanels,
  categoryFilterFacet,
  categoryFilterName
}: GetCategoryFilterPathnameArgs): string => {
  const facet = filterPanels.find(({ name }) => name === categoryFilterFacet);
  const categoryFilter =
    isValueFacetFilter(facet) &&
    facet.values.find(({ displayName }) => displayName === categoryFilterName);
  return (categoryFilter && categoryFilter.url) || '';
};

export const filterToggleCategory =
  (args: FilterToggleCategoryParams) => (dispatch: Dispatch, getState: () => PageState) => {
    const { currentPage, location } = getState();
    if (!isFilterPage(currentPage)) {
      return;
    }

    const { isCategoryFilterPage, categoryFilterFacet, categoryFilterName, filterPanels } =
      currentPage;

    const resetCategoryFacet = isCategoryFilterPage && args.facet === categoryFilterFacet;
    const basePathname =
      resetCategoryFacet &&
      getCategoryFilterPathname({
        filterPanels,
        categoryFilterFacet,
        categoryFilterName
      });
    const newLocation = createHref({ ...location, pathname: basePathname || location.pathname });

    if (args.facets) {
      args.facets.forEach(f => {
        if (newLocation.search[f.name]) {
          delete newLocation.search[f.name];
        }
      });
    } else {
      if (newLocation.search[args.facet]) {
        delete newLocation.search[args.facet];
      }
    }

    void reduxDispatchEvent(historyPushState({ url: newLocation }));
    dispatch(GtmFilterChange());

    return dispatch({
      type: FILTER_TOGGLE_CATEGORY,
      ...args
    });
  };

export const filterToggleReset = () => (dispatch: Dispatch, getState: () => PageState) => {
  const { currentPage, location } = getState();

  if (!isFilterPage(currentPage)) {
    return;
  }

  const mapName = (f: FacetFilter): string[] =>
    invokeIfExists(
      {
        [FilterDisplayType.Group]: () => (f as FacetGroup).facets.flatMap(mapName),
        [FilterDisplayType.Standard]: () => [f.name],
        [FilterDisplayType.SimpleSlider]: () => [f.name],
        [FilterDisplayType.Category]: () => [f.name],
        [FilterDisplayType.NoHeader]: () => [f.name]
      },
      f.type
    );

  const newLocation = createHref({
    ...location,
    pathname: location.pathname
  });

  currentPage.filterPanels
    .flatMap(mapName)
    .forEach(name => !!newLocation.search[name] && delete newLocation.search[name]);

  void reduxDispatchEvent(historyPushState({ url: newLocation }));
  dispatch(GtmFilterChange());

  return dispatch({
    type: FILTER_TOGGLE_RESET
  });
};
